/**
 * V67: 알림 마스터 페이지 (system_admin 전용)
 *
 * - 좌측: 알림 type 목록 (카테고리 필터 + 검색)
 * - 우측: 선택된 type 의 메타(설명/트리거/카테고리) + 추가 수신자 관리
 *
 * 추가 방식:
 *   1) 사용자 검색 후 선택 → USER 타입으로 추가
 *   2) 직접 이메일 입력 (DL/외부 이메일 포함) → EMAIL 타입으로 추가
 *
 * 기존 알림 발송 로직 변경 없음 — 본 페이지는 추가 수신자만 관리.
 */

import React, { useMemo, useState } from 'react';
import { Bell, Search, Plus, Trash2, X, RefreshCw } from 'lucide-react';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import {
  useNotificationTypes,
  NotificationTypeDoc,
  AdditionalRecipient,
  callAddNotificationRecipient,
  callRemoveNotificationRecipient,
  callSearchNotificationUsers,
  SearchUserItem,
} from '../../shared/hooks/useNotificationTypes';
import { ModalOverlay } from '../../shared/components/ModalOverlay';

const CATEGORY_LABELS: Record<string, string> = {
  approval: '승인',
  leave: '휴가',
  certificate: '증명서',
  evaluation: '평가',
  contract: '계약',
  probation: '수습',
  workSchedule: '근무일정',
  attendance: '근태',
  transfer: '전배',
  announcement: '공지',
  interview: '면담',
  misc: '기타',
};

function categoryLabel(c: string): string {
  return CATEGORY_LABELS[c] || c;
}

// V68: 실제 알림 hook 에서 사용 중인 DL key 화이트리스트.
// V68 HR DL 전면 KR/CN 분리 완료 후 — DL:hr_works (legacy) / DL:hr_leave (dead) 모두 실발송 사용처 0건 → UI 미노출.
// 신규 DL 도입 시 본 배열 갱신 필요.
const USED_DL_KEYS = ['DL:hr_works_kr', 'DL:hr_works_cn'];

export function AdminNotificationSettingsPage() {
  const { hasRole, isLoading: authLoading } = useAuthorization();
  const isSystemAdmin = hasRole('system_admin');

  const { items, dlMap, projectId, isLoading, error, refetch } = useNotificationTypes();

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);

  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (categoryFilter !== 'all' && it.category !== categoryFilter) return false;
      if (searchText) {
        const lower = searchText.toLowerCase();
        const haystack = `${it.type} ${it.title} ${it.description}`.toLowerCase();
        if (!haystack.includes(lower)) return false;
      }
      return true;
    });
  }, [items, categoryFilter, searchText]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const it of items) set.add(it.category);
    return Array.from(set).sort();
  }, [items]);

  const selected: NotificationTypeDoc | null = useMemo(() => {
    if (!selectedType) return null;
    return items.find((it) => it.type === selectedType) || null;
  }, [items, selectedType]);

  if (authLoading) return <div style={{ padding: 24 }}>로딩 중...</div>;
  if (!isSystemAdmin) {
    return (
      <div style={{ padding: 24, color: 'var(--color-red-600)' }}>
        접근 권한이 없습니다. system_admin 만 이용 가능합니다.
      </div>
    );
  }

  return (
    <div style={{ padding: 24, height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Bell size={20} />
        <h1 style={{ margin: 0, fontSize: 18 }}>알림 마스터</h1>
        <span style={{ fontSize: 12, padding: '2px 8px', background: 'var(--color-gray-100)', borderRadius: 12, color: 'var(--color-gray-700)' }}>
          총 {items.length}개 알림
        </span>
        <button onClick={() => refetch()} style={iconBtnStyle} title="새로고침">
          <RefreshCw size={14} />
        </button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginBottom: 8 }}>
        각 알림 type의 기본 수신자는 유지되며, 추가 수신자(추가 발송)만 본 페이지에서 관리합니다. (system_admin 전용)
      </div>

      {/* V68: 현재 환경 DL 매핑 (읽기 전용) — 실제 알림 hook 에서 사용 중인 키만 노출 */}
      {(() => {
        const visibleEntries = Object.entries(dlMap || {}).filter(([k]) => USED_DL_KEYS.includes(k));
        if (visibleEntries.length === 0) return null;
        return (
          <div style={{ fontSize: 11, color: 'var(--color-gray-700)', padding: '8px 10px', background: 'var(--color-gray-50)', border: '1px solid var(--color-gray-200)', borderRadius: 4, marginBottom: 16, fontFamily: 'monospace' }}>
            <strong>현재 환경 DL 매핑</strong>{projectId ? ` (project=${projectId})` : ''}:&nbsp;
            {visibleEntries.map(([k, v]) => `${k} → ${v.email}`).join(' | ')}
          </div>
        );
      })()}

      {error && (
        <div style={{ color: 'var(--color-red-600)', padding: 8, background: 'var(--color-red-50)', borderRadius: 4, marginBottom: 8 }}>
          {error.message}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
        {/* 좌측: 알림 목록 */}
        <div style={{ flex: '0 0 380px', display: 'flex', flexDirection: 'column', border: '1px solid var(--color-gray-200)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: 12, borderBottom: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <Search size={14} color="var(--color-gray-500)" />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="type / 제목 / 설명 검색"
                style={{ ...inputStyle, flex: 1 }}
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ ...inputStyle, width: '100%' }}
            >
              <option value="all">전체 카테고리 ({items.length})</option>
              {categories.map((c) => (
                <option key={c} value={c}>{categoryLabel(c)} ({items.filter((it) => it.category === c).length})</option>
              ))}
            </select>
          </div>

          <div style={{ flex: 1, overflow: 'auto' }}>
            {isLoading ? (
              <div style={{ padding: 12, color: 'var(--color-gray-500)' }}>조회 중...</div>
            ) : filteredItems.length === 0 ? (
              <div style={{ padding: 12, color: 'var(--color-gray-500)' }}>표시할 알림 없음</div>
            ) : (
              filteredItems.map((it) => {
                const isSelected = selectedType === it.type;
                const recipCount = it.additionalRecipients.length;
                return (
                  <div
                    key={it.type}
                    onClick={() => setSelectedType(it.type)}
                    style={{
                      padding: '10px 12px',
                      borderBottom: '1px solid var(--color-gray-100)',
                      cursor: 'pointer',
                      background: isSelected ? 'var(--color-blue-50)' : 'transparent',
                      borderLeft: isSelected ? '3px solid var(--color-blue-600)' : '3px solid transparent',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{it.title}</span>
                      {recipCount > 0 && (
                        <span style={{ fontSize: 11, padding: '1px 6px', background: 'var(--color-green-100)', color: 'var(--color-green-700)', borderRadius: 10 }}>
                          +{recipCount}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-gray-500)', marginTop: 2 }}>
                      {categoryLabel(it.category)} · <span style={{ fontFamily: 'monospace' }}>{it.type}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* 우측: 상세 */}
        <div style={{ flex: 1, border: '1px solid var(--color-gray-200)', borderRadius: 8, overflow: 'auto', padding: 16 }}>
          {!selected ? (
            <div style={{ color: 'var(--color-gray-500)', textAlign: 'center', marginTop: 60 }}>
              왼쪽 목록에서 알림을 선택하세요.
            </div>
          ) : (
            <DetailPanel
              item={selected}
              dlMap={dlMap}
              projectId={projectId}
              onAddClick={() => setShowAddModal(true)}
              onRefresh={refetch}
            />
          )}
        </div>
      </div>

      {showAddModal && selected && (
        <AddRecipientModal
          notificationType={selected.type}
          existingKeys={selected.additionalRecipients.map((r) => `${r.email.toLowerCase()}|${r.companyCode || 'ALL'}`)}
          onClose={() => setShowAddModal(false)}
          onAdded={async () => {
            setShowAddModal(false);
            await refetch();
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// 상세 패널
// ============================================================

function DetailPanel({
  item,
  dlMap,
  projectId,
  onAddClick,
  onRefresh,
}: {
  item: NotificationTypeDoc;
  dlMap?: Record<string, { email: string; name: string }>;
  projectId?: string;
  onAddClick: () => void;
  onRefresh: () => Promise<void>;
}) {
  // V68: 본 type 의 정책 label 이 'HR DL' 키워드를 포함하면 현재 환경 DL 매핑 박스 노출.
  //   단, leave_approved_share 처럼 라벨에 "HR DL 발송 없음" 명시된 경우는 박스 미노출.
  //   USED_DL_KEYS 화이트리스트로 hr_leave 등 미사용 키 제외.
  const labelText = item.defaultRecipientsLabel || '';
  const usesHrDl = labelText.includes('HR DL') && !labelText.includes('HR DL 발송 없음');
  const dlEntries = (dlMap ? Object.entries(dlMap) : []).filter(([k]) => USED_DL_KEYS.includes(k));
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>{item.title}</h2>
        <div style={{ fontSize: 11, color: 'var(--color-gray-500)', marginTop: 4, fontFamily: 'monospace' }}>
          {item.type}
        </div>
      </div>

      <Section title="설명">
        <div style={{ fontSize: 13, color: 'var(--color-gray-700)', whiteSpace: 'pre-wrap' }}>
          {item.description || '-'}
        </div>
      </Section>

      <Section title="발생 시점">
        <div style={{ fontSize: 13, color: 'var(--color-gray-700)', whiteSpace: 'pre-wrap' }}>
          {item.trigger || '-'}
        </div>
      </Section>

      <Section title="카테고리">
        <span style={{ fontSize: 12, padding: '2px 8px', background: 'var(--color-gray-100)', borderRadius: 4 }}>
          {categoryLabel(item.category)}
        </span>
      </Section>

      <Section title="기본 수신자 (시스템 자동 결정)">
        <div style={{ fontSize: 13, color: 'var(--color-gray-700)', whiteSpace: 'pre-wrap' }}>
          {item.defaultRecipientsLabel || '(설명 없음)'}
        </div>

        {/* V68: 본 type 정책에 'HR DL' 사용 시 현재 환경 DL 매핑 노출 */}
        {usesHrDl && dlEntries.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--color-gray-700)', padding: '6px 10px', marginTop: 8, background: 'var(--color-gray-50)', border: '1px solid var(--color-gray-200)', borderRadius: 4, fontFamily: 'monospace' }}>
            현재 환경 DL 매핑{projectId ? ` (project=${projectId})` : ''}: {dlEntries.map(([k, v]) => `${k} → ${v.email}`).join(' | ')}
          </div>
        )}

        <div style={{ fontSize: 11, color: 'var(--color-gray-500)', marginTop: 6, fontStyle: 'italic' }}>
          ※ 기본 수신자는 코드 정책으로 자동 결정됩니다. 신청자/승인자/조직 DL/조직장 등 일부 항목은 이벤트 발생 시점에 결정되어 사전 조회 불가합니다. 본 페이지에서는 수정할 수 없으며, 추가 수신자만 아래에서 관리합니다.
        </div>
      </Section>

      <Section
        title={`추가 수신자 (${item.additionalRecipients.length}명)`}
        right={
          <button onClick={onAddClick} style={btnPrimarySmall}>
            <Plus size={12} /> 추가
          </button>
        }
      >
        {item.additionalRecipients.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>등록된 추가 수신자가 없습니다.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)' }}>
                <Th>종류</Th>
                <Th>회사</Th>
                <Th>이메일</Th>
                <Th>이름</Th>
                <Th>등록일</Th>
                <Th align="right">액션</Th>
              </tr>
            </thead>
            <tbody>
              {item.additionalRecipients.map((r, idx) => (
                <RecipientRow
                  key={`${r.email}|${r.companyCode || 'ALL'}|${idx}`}
                  notificationType={item.type}
                  recipient={r}
                  onRemoved={onRefresh}
                />
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function RecipientRow({
  notificationType,
  recipient,
  onRemoved,
}: {
  notificationType: string;
  recipient: AdditionalRecipient;
  onRemoved: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const company = recipient.companyCode || 'ALL';

  const handleRemove = async () => {
    if (!window.confirm(`${recipient.email} (${company}) 을(를) 삭제하시겠습니까?`)) return;
    setBusy(true);
    try {
      // V68: companyCode 전달 — 같은 이메일의 다른 회사 entry 는 보존
      await callRemoveNotificationRecipient({ notificationType, email: recipient.email, companyCode: company });
      await onRemoved();
    } catch (e: any) {
      alert('삭제 실패: ' + (e.message || e));
    } finally {
      setBusy(false);
    }
  };

  // V68: 회사별 배지 색상
  const companyStyle = company === 'KR'
    ? { background: '#dcfce7', color: '#166534' }
    : company === 'CN'
    ? { background: '#fee2e2', color: '#991b1b' }
    : { background: 'var(--color-gray-100)', color: 'var(--color-gray-700)' };

  return (
    <tr style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
      <Td>
        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: recipient.type === 'USER' ? 'var(--color-blue-100)' : 'var(--color-orange-100)', color: recipient.type === 'USER' ? 'var(--color-blue-700)' : 'var(--color-orange-700)' }}>
          {recipient.type}
        </span>
      </Td>
      <Td>
        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, ...companyStyle }}>
          {company}
        </span>
      </Td>
      <Td style={{ fontFamily: 'monospace' }}>{recipient.email}</Td>
      <Td>{recipient.displayName || '-'}</Td>
      <Td style={{ fontSize: 11, color: 'var(--color-gray-600)' }}>
        {(recipient.addedAt || '').slice(0, 16).replace('T', ' ')}
      </Td>
      <Td align="right">
        <button onClick={handleRemove} disabled={busy} title="삭제" style={{ ...iconBtnStyle, color: 'var(--color-red-600)' }}>
          <Trash2 size={12} />
        </button>
      </Td>
    </tr>
  );
}

// ============================================================
// 추가 모달
// ============================================================

function AddRecipientModal({
  notificationType,
  existingKeys,
  onClose,
  onAdded,
}: {
  notificationType: string;
  /** V68: 형식 "<email>|<companyCode>" (이미 등록된 (email,company) 키) */
  existingKeys: string[];
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const [tab, setTab] = useState<'user' | 'email'>('user');
  const [err, setErr] = useState<string>('');
  const [busy, setBusy] = useState(false);
  // V68: 적용 회사 선택 (기본 ALL)
  const [companyCode, setCompanyCode] = useState<'ALL' | 'KR' | 'CN'>('ALL');

  // USER 검색 상태
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUserItem[]>([]);
  const [searching, setSearching] = useState(false);

  // EMAIL 직접 입력 상태
  const [emailInput, setEmailInput] = useState('');
  const [displayNameInput, setDisplayNameInput] = useState('');

  const handleSearch = async () => {
    if (searchText.trim().length < 2) {
      setErr('2자 이상 입력하세요.');
      return;
    }
    setErr('');
    setSearching(true);
    try {
      const users = await callSearchNotificationUsers(searchText.trim());
      setSearchResults(users);
    } catch (e: any) {
      setErr(e.message || '검색 실패');
    } finally {
      setSearching(false);
    }
  };

  const handleAddUser = async (u: SearchUserItem) => {
    const key = `${u.email.toLowerCase()}|${companyCode}`;
    if (existingKeys.includes(key)) {
      setErr(`이미 등록된 이메일: ${u.email} (${companyCode})`);
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await callAddNotificationRecipient({
        notificationType,
        recipientType: 'USER',
        email: u.email,
        userId: u.userId,
        displayName: u.displayName,
        companyCode,
      });
      await onAdded();
    } catch (e: any) {
      setErr(e.message || '추가 실패');
    } finally {
      setBusy(false);
    }
  };

  const handleAddEmail = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) {
      setErr('이메일을 입력하세요.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr('이메일 형식이 올바르지 않습니다.');
      return;
    }
    const key = `${email}|${companyCode}`;
    if (existingKeys.includes(key)) {
      setErr(`이미 등록된 이메일: ${email} (${companyCode})`);
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await callAddNotificationRecipient({
        notificationType,
        recipientType: 'EMAIL',
        email,
        displayName: displayNameInput.trim() || undefined,
        companyCode,
      });
      await onAdded();
    } catch (e: any) {
      setErr(e.message || '추가 실패');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} style={overlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: 15 }}>추가 수신자 등록: {notificationType}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>

        {/* V68: 적용 회사 선택 (기본 ALL) */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)' }}>
          <div style={{ fontSize: 12, color: 'var(--color-gray-700)', marginBottom: 6, fontWeight: 600 }}>
            적용 회사 (Company Filter)
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
            {(['ALL', 'KR', 'CN'] as const).map((c) => (
              <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="companyCode"
                  value={c}
                  checked={companyCode === c}
                  onChange={() => setCompanyCode(c)}
                  disabled={busy}
                />
                <span style={{ fontWeight: companyCode === c ? 600 : 400 }}>
                  {c === 'ALL' ? 'ALL (모든 회사)' : c}
                </span>
              </label>
            ))}
          </div>
          <div style={{ fontSize: 10, color: 'var(--color-gray-500)', marginTop: 4, fontStyle: 'italic' }}>
            ALL = 모든 회사 알림 수신 / KR·CN = 해당 회사 알림만 수신
          </div>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-gray-200)' }}>
          <TabButton active={tab === 'user'} onClick={() => setTab('user')}>사용자 검색</TabButton>
          <TabButton active={tab === 'email'} onClick={() => setTab('email')}>직접 이메일 입력</TabButton>
        </div>

        <div style={{ padding: 16, minHeight: 320 }}>
          {tab === 'user' ? (
            <div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  placeholder="이름 / 이메일 / 조직 (2자 이상)"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button onClick={handleSearch} disabled={searching} style={btnSecondary}>
                  {searching ? '검색 중...' : '검색'}
                </button>
              </div>
              <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--color-gray-200)', borderRadius: 4 }}>
                {searchResults.length === 0 ? (
                  <div style={{ padding: 12, color: 'var(--color-gray-500)', fontSize: 12 }}>검색 결과 없음</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--color-gray-50)' }}>
                        <Th>이름</Th>
                        <Th>이메일</Th>
                        <Th>조직</Th>
                        <Th align="right">액션</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((u) => {
                        const dup = existingKeys.includes(`${u.email.toLowerCase()}|${companyCode}`);
                        return (
                          <tr key={u.userId} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                            <Td>{u.displayName || '-'}</Td>
                            <Td style={{ fontFamily: 'monospace' }}>{u.email}</Td>
                            <Td style={{ fontSize: 11, color: 'var(--color-gray-600)' }}>{u.orgUnitName || '-'}</Td>
                            <Td align="right">
                              <button
                                onClick={() => handleAddUser(u)}
                                disabled={busy || dup}
                                style={dup ? btnDisabled : btnPrimarySmall}
                              >
                                {dup ? '등록됨' : '+ 추가'}
                              </button>
                            </Td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Field label="이메일 (외부/DL 포함)">
                <input
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="example@ngle.co.kr"
                  style={inputStyle}
                />
              </Field>
              <Field label="표시 이름 (선택)">
                <input
                  value={displayNameInput}
                  onChange={(e) => setDisplayNameInput(e.target.value)}
                  placeholder="예: 운영팀 DL"
                  style={inputStyle}
                />
              </Field>
              <button onClick={handleAddEmail} disabled={busy} style={btnPrimary}>
                {busy ? '추가 중...' : '추가'}
              </button>
            </div>
          )}
          {err && <div style={{ color: 'var(--color-red-600)', fontSize: 12, marginTop: 8 }}>{err}</div>}
        </div>

        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={busy} style={btnSecondary}>닫기</button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================================
// 작은 컴포넌트들 / 스타일
// ============================================================

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13, color: 'var(--color-gray-700)' }}>{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
      <span style={{ color: 'var(--color-gray-700)' }}>{label}</span>
      {children}
    </label>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) {
  return (
    <th style={{ padding: '6px 8px', textAlign: align || 'left', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--color-gray-700)' }}>
      {children}
    </th>
  );
}
function Td({ children, align, style }: { children: React.ReactNode; align?: 'left' | 'right' | 'center'; style?: React.CSSProperties }) {
  return (
    <td style={{ padding: '6px 8px', textAlign: align || 'left', verticalAlign: 'middle', ...style }}>
      {children}
    </td>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '10px 12px',
        background: active ? 'white' : 'var(--color-gray-50)',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-blue-600)' : '2px solid transparent',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

const iconBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 4,
  padding: '4px 6px',
  cursor: 'pointer',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 4,
  fontSize: 12,
};

const btnPrimary: React.CSSProperties = {
  padding: '8px 16px',
  background: 'var(--color-blue-600)',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};

const btnPrimarySmall: React.CSSProperties = {
  padding: '4px 10px',
  background: 'var(--color-blue-600)',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 11,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const btnSecondary: React.CSSProperties = {
  padding: '6px 14px',
  background: 'white',
  color: 'var(--color-gray-700)',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 13,
};

const btnDisabled: React.CSSProperties = {
  padding: '4px 10px',
  background: 'var(--color-gray-200)',
  color: 'var(--color-gray-500)',
  border: 'none',
  borderRadius: 4,
  cursor: 'not-allowed',
  fontSize: 11,
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  background: 'white',
  borderRadius: 8,
  width: '90vw',
  maxWidth: 720,
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};
